import type { Points } from "../../components/SvgCanvas/renderers";
import type { WorkerResponse, WorkerErrorResponse } from "./delaunayWorker";
import { createWorkerError } from "../../types/errors";

type WorkerTask = {
	id: string;
	points: Points;
	maxEdgeLength: number;
	resolve: (value: WorkerResponse) => void;
	reject: (reason: Error) => void;
};

// Helper function to check if response is an error
const isWorkerErrorResponse = (data: unknown): data is WorkerErrorResponse => {
	return (
		typeof data === "object" &&
		data !== null &&
		"error" in data &&
		"type" in data
	);
};

export class WorkerManager {
	private static instance: WorkerManager | null = null;
	private workers: Worker[] = [];
	private taskQueue: WorkerTask[] = [];
	private busyWorkers: Set<Worker> = new Set();
	private workerToTask: Map<Worker, WorkerTask> = new Map();
	private readonly maxWorkers: number;

	private constructor(maxWorkers = navigator.hardwareConcurrency || 4) {
		this.maxWorkers = maxWorkers;
	}

	public static getInstance(): WorkerManager {
		if (!WorkerManager.instance) {
			WorkerManager.instance = new WorkerManager();
		}
		return WorkerManager.instance;
	}

	private createWorker(): Worker {
		const worker = new Worker(
			new URL("./delaunayWorker.ts", import.meta.url),
		);

		worker.onmessage = (
			event: MessageEvent<WorkerResponse | WorkerErrorResponse>,
		) => {
			if (isWorkerErrorResponse(event.data)) {
				this.handleWorkerError(worker, new Error(event.data.error));
			} else {
				this.handleWorkerComplete(worker, event.data);
			}
		};

		worker.onerror = (error) => {
			console.error("Worker error:", error);
			this.handleWorkerError(worker, error);
		};

		return worker;
	}

	private getAvailableWorker(): Worker | null {
		// Return existing idle worker
		const availableWorker = this.workers.find(
			(w) => !this.busyWorkers.has(w),
		);
		if (availableWorker) return availableWorker;

		// Create new worker if under limit
		if (this.workers.length < this.maxWorkers) {
			const newWorker = this.createWorker();
			this.workers.push(newWorker);
			return newWorker;
		}

		return null;
	}

	private handleWorkerComplete(worker: Worker, result: WorkerResponse) {
		const task = this.workerToTask.get(worker);
		if (task) {
			task.resolve(result);
			this.workerToTask.delete(worker);
			this.removeTaskFromQueue(task.id);
		}
		this.busyWorkers.delete(worker);
		this.processNextTask();
	}

	private handleWorkerError(worker: Worker, error: Error | ErrorEvent) {
		const task = this.workerToTask.get(worker);
		if (task) {
			const workerError = createWorkerError(
				error instanceof Error
					? error.message
					: error.message || "Worker error",
				{
					workerId: this.workers.indexOf(worker).toString(),
					taskId: task.id,
					originalError: error,
				},
			);
			task.reject(new Error(workerError.message));
			this.workerToTask.delete(worker);
			this.removeTaskFromQueue(task.id);
		}
		this.busyWorkers.delete(worker);

		// Replace failed worker
		this.replaceWorker(worker);
		this.processNextTask();
	}

	private removeTaskFromQueue(taskId: string) {
		const index = this.taskQueue.findIndex((task) => task.id === taskId);
		if (index !== -1) {
			this.taskQueue.splice(index, 1);
		}
	}

	private replaceWorker(failedWorker: Worker) {
		const index = this.workers.indexOf(failedWorker);
		if (index !== -1) {
			failedWorker.terminate();
			this.workers[index] = this.createWorker();
		}
	}

	private processNextTask() {
		if (this.taskQueue.length === 0) return;

		const worker = this.getAvailableWorker();
		if (!worker) return; // All workers busy

		const task = this.taskQueue[0];
		this.busyWorkers.add(worker);
		this.workerToTask.set(worker, task);
		worker.postMessage({
			points: task.points,
			maxEdgeLength: task.maxEdgeLength,
		});
	}

	private generateTaskId(): string {
		return `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
	}

	public async processDelaunay(
		points: Points,
		maxEdgeLength: number,
	): Promise<WorkerResponse> {
		return new Promise((resolve, reject) => {
			const task: WorkerTask = {
				id: this.generateTaskId(),
				points,
				maxEdgeLength,
				resolve,
				reject,
			};
			this.taskQueue.push(task);
			this.processNextTask();
		});
	}

	public terminateAll() {
		this.workers.forEach((worker) => worker.terminate());
		this.workers = [];
		this.busyWorkers.clear();
		this.workerToTask.clear();
		this.taskQueue = [];
	}
}
